import React, { useEffect, useState } from 'react';
import Form from 'react-bootstrap/Form';
import { useSelector } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import Swal from 'sweetalert2';
import { IReducer } from '../../../../../../../redux/configureStore';
import { ReportType, TipoRelatorio } from '../../../interface';
import {
  requestReport,
  RequestReportError,
} from '../../../services/reportsService';
import DateQuickButtons from './DateQuickButtons';
import HintBox from './HintBox';
import PartnerSelect from './PartnerSelect';

const MAX_PERIODO_DIAS = 31;
const MS_POR_DIA = 86400000;

const FormCard = styled.div`
  background-color: ${(props) => props.theme.colors.white};
  border-radius: ${(props) => props.theme.border.default};
  box-shadow: ${(props) => props.theme.shadow.default};
  padding: 1.25rem 1.5rem;
  margin: 0 0 1rem 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-sizing: border-box;
  width: 100%;
`;

const Title = styled.h2`
  font-size: ${(props) => props.theme.fontSize.big};
  color: ${(props) => props.theme.colors.mainColor};
  font-weight: 700;
  margin: 0;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-end;
  margin: 0 0 0.75rem 0;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 12rem;
`;

const Label = styled.label`
  font-size: ${(props) => props.theme.fontSize.small};
  color: ${(props) => props.theme.colors.lightGray};
  font-weight: 600;
  margin: 0;
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const SubmitButton = styled.button`
  background-color: ${(props) => props.theme.colors.mainColor};
  color: ${(props) => props.theme.colors.white};
  border: none;
  border-radius: ${(props) => props.theme.border.default};
  padding: 0.5rem 1.5rem;
  font-size: ${(props) => props.theme.fontSize.medium};
  font-weight: 700;
  cursor: pointer;
  height: 2.5rem;
  min-width: 12rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: background-color
    ${(props) => props.theme.transitionDuration.default};

  &:hover:not(:disabled) {
    background-color: ${(props) => props.theme.colors.hover};
  }

  &:disabled {
    cursor: wait;
    opacity: 0.8;
  }
`;

const Spinner = styled.span`
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: ${(props) => props.theme.colors.white};
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

interface Props {
  onCreated: () => void;
  tipoRelatorio: TipoRelatorio;
}

function RequestForm({ onCreated, tipoRelatorio }: Props) {
  const userProfile = useSelector((reducer: IReducer) => reducer.userProfile);
  const partnerProfile = useSelector(
    (reducer: IReducer) => reducer.partnerProfile
  );

  const [type, setType] = useState<ReportType>('sintetico');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [partnerId, setPartnerId] = useState<number | null>(null);
  const [partnerName, setPartnerName] = useState('');
  const [requestedBy, setRequestedBy] = useState('');
  // Trava o botao e mostra spinner enquanto a request roda — evita
  // submit duplo e da feedback imediato no clique (antes a tela ficava
  // parada ate o popup de sucesso aparecer).
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!requestedBy && userProfile.nomeRazaoSocial) {
      setRequestedBy(userProfile.nomeRazaoSocial);
    }
  }, [userProfile.nomeRazaoSocial]);

  // Somente admin Fiducia (nivel 2 E parceiro 1) pode escolher parceiros
  // diferentes; pra qualquer outro caso o parceiro fica fixado no do
  // usuario logado (espelho da regra do PartnerSelect).
  const isAdminFiducia = userProfile.nivel === 2 && userProfile.partnerId === 1;

  useEffect(() => {
    if (
      !isAdminFiducia &&
      partnerProfile.idLegado &&
      partnerId !== partnerProfile.idLegado
    ) {
      setPartnerId(partnerProfile.idLegado);
      setPartnerName(partnerProfile.companyName || 'Meu parceiro');
    }
  }, [isAdminFiducia, partnerProfile.idLegado]);

  function handlePartner(id: number, name: string) {
    setPartnerId(id || null);
    setPartnerName(name);
  }

  function handleDateRange(start: string, end: string) {
    setDateStart(start);
    setDateEnd(end);
  }

  function resetForm() {
    setType('sintetico');
    setDateStart('');
    setDateEnd('');
    // Reseta o parceiro apenas pra admin Fiducia (que pode escolher); pra
    // usuarios comuns, o parceiro fica fixado no do proprio usuario.
    if (isAdminFiducia) {
      setPartnerId(null);
      setPartnerName('');
    }
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!dateStart || !dateEnd) {
      Swal.fire({
        icon: 'warning',
        title: 'Datas obrigatórias',
        text: 'Preencha as datas inicial e final.',
      });
      return;
    }
    if (dateStart > dateEnd) {
      Swal.fire({
        icon: 'warning',
        title: 'Período inválido',
        text: 'A data inicial deve ser anterior ou igual à data final.',
      });
      return;
    }
    const dias =
      (new Date(dateEnd).getTime() - new Date(dateStart).getTime()) /
      MS_POR_DIA;
    if (dias > MAX_PERIODO_DIAS) {
      Swal.fire({
        icon: 'warning',
        title: 'Período muito longo',
        text: `O intervalo entre as datas não pode passar de ${MAX_PERIODO_DIAS} dias. Divida em mais solicitações se precisar de um período maior.`,
      });
      return;
    }
    if (!partnerId) {
      Swal.fire({
        icon: 'warning',
        title: 'Parceiro obrigatório',
        text: 'Selecione um parceiro.',
      });
      return;
    }

    if (submitting) return;
    setSubmitting(true);
    try {
      await requestReport({
        type,
        tipoRelatorio,
        partnerId,
        partnerName: partnerName || `Parceiro ${partnerId}`,
        dateStart,
        dateEnd,
        requestedBy: requestedBy.trim() || userProfile.nomeRazaoSocial || '—',
      });
    } catch (err) {
      if (err instanceof RequestReportError) {
        const isDuplicado = err.codigoErro === 'DUPLICADO';
        Swal.fire({
          icon: isDuplicado ? 'info' : 'warning',
          title: isDuplicado
            ? 'Solicitação já existe'
            : 'Não foi possível criar a solicitação',
          text: err.retorno,
        });
        return;
      }
      Swal.fire({
        icon: 'error',
        title: 'Erro inesperado',
        text: 'Tente novamente em instantes.',
      });
      return;
    } finally {
      setSubmitting(false);
    }

    Swal.fire({
      icon: 'success',
      title: 'Solicitação criada!',
      text: 'Pode levar alguns minutos. Acompanhe o status na tabela logo abaixo.',
      timer: 3000,
    });

    resetForm();
    onCreated();
  }

  const tituloRelatorio =
    tipoRelatorio === 'tarifas'
      ? 'Solicitar relatório de Tarifas Gerais'
      : 'Solicitar relatório de Tarifas de Recebimento';

  return (
    <FormCard>
      <Title>{tituloRelatorio}</Title>
      <HintBox tipoRelatorio={tipoRelatorio} />
      <Form onSubmit={handleSubmit}>
        <Row>
          <Field>
            <Label>Tipo de relatório</Label>
            <Form.Control
              as="select"
              value={type}
              onChange={(e) => setType(e.target.value as ReportType)}>
              <option value="sintetico">Sintético</option>
              <option value="analitico">Analítico</option>
            </Form.Control>
          </Field>
          <Field>
            <Label>Data inicial</Label>
            <Form.Control
              type="date"
              value={dateStart}
              onChange={(e) => setDateStart(e.target.value)}
            />
          </Field>
          <Field>
            <Label>Data final</Label>
            <Form.Control
              type="date"
              value={dateEnd}
              onChange={(e) => setDateEnd(e.target.value)}
            />
          </Field>
        </Row>
        <Row>
          <DateQuickButtons onPick={handleDateRange} />
        </Row>
        <Row>
          <PartnerSelect value={partnerId} onChange={handlePartner} />
          <Field>
            <Label>Solicitado por</Label>
            <Form.Control
              type="text"
              value={requestedBy}
              placeholder="Nome do solicitante"
              onChange={(e) => setRequestedBy(e.target.value)}
            />
          </Field>
        </Row>
        <Row>
          <SubmitButton type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner />
                Enviando…
              </>
            ) : (
              'Solicitar relatório'
            )}
          </SubmitButton>
        </Row>
      </Form>
    </FormCard>
  );
}

export default RequestForm;
